/**
 * Route plans: the map's route network planned by the autonomy team's offline planner
 * (tcr global_path_harness, replan_network.py: SE(2) A* + cubic B-spline, the footprint checked
 * every 1 cm / 1°, swerve timing), run by apps/route-planner. The platform sends the map and its
 * waypoints as poses; the planner connects them the way pose_picker does (graph_pairs: every
 * fixture to the home and its nearest navigate node, navigate nodes chained) and returns a
 * route_network.json the robot runs as is.
 *
 *   queued → running → succeeded (every connection planned)
 *                    → partial   (some connections failed; the rest form the network)
 *                    → refused   (the planner wrote nothing: a pose collides, not one home, ...)
 *                    → failed    (the planner could not be reached or crashed)
 *
 * A plan belongs to the map version and waypoint poses it was made from (posesFingerprint).
 * When either changes it is stale: deployments then ship the drawn paths instead, and the
 * editor asks for a re-plan.
 *
 * A network the harness already planned offline can be imported instead (POST
 * .../route-plans/import, RouteNetworkImport): it is stored as a finished plan when its map and
 * nodes match the map's current version and waypoints, and goes stale like any other.
 */
import { z } from "zod";
export const RoutePlanStatus = z.enum(["queued", "running", "succeeded", "partial", "refused", "failed"]);
export const ACTIVE_ROUTE_PLAN_STATUSES = ["queued", "running"];
/** Plans whose network can be deployed (when not stale). */
export const USABLE_ROUTE_PLAN_STATUSES = ["succeeded", "partial"];
/**
 * Names the planner can address (apps/route-planner NAME_RE): pose_picker keys, so no whitespace
 * and no "->" (the pair separator). Waypoints named otherwise must be renamed before planning.
 */
export const PLANNER_NAME_RE = /^[A-Za-z0-9_][A-Za-z0-9_.-]{0,63}$/;
export function plannerNameProblem(name) {
    if (/\s/.test(name))
        return "contains whitespace";
    if (name.includes("->"))
        return 'contains "->"';
    if (!PLANNER_NAME_RE.test(name))
        return "may only use letters, digits, _ . - (up to 64, not starting with . or -)";
    return null;
}
const finite = z.number().finite();
/** RouteNetwork::load reads node ids with get<int>. */
const nodeId = z.number().int().min(-2147483648, "Must fit a 32-bit integer").max(2147483647, "Must fit a 32-bit integer");
const NONNEGATIVE = "Must be 0 or more: the robot's route search skips negative weights";
/** An edge weight: the robot's route search skips edges whose weight is not finite and ≥ 0. */
const weight = finite.nonnegative(NONNEGATIVE);
/**
 * A route_network.json global_path_harness planned offline (replan_network.py), the body of
 * POST .../route-plans/import. Checked: what nav2_route_network_planner needs to load it (unique
 * 32-bit integer node ids, edges between listed nodes, x / y / yaw of one length with at least
 * two samples, feasibility violations as objects, numeric travel_time / path_length, which it
 * reads with get<double>) and to route on it: every edge's cost.total, its default edge_weight
 * (a missing cost is infinite there, so the edge is never driven). The harness writes all of
 * these; a file without them is refused rather than completed with guesses. Also the node poses,
 * kinds and names the import matches against the waypoints. Everything else passes through to
 * the robot unchanged.
 */
export const RouteNetworkImport = z
    .object({
    schema_version: z.literal(1),
    frame_id: z.literal("map"),
    metadata: z.record(z.unknown()).optional(),
    /** The map it was planned on (map.yaml's values); compared with the map's current version. */
    map: z.object({ resolution: finite.positive(), origin: z.array(finite).min(2).max(3) }).passthrough().optional(),
    nodes: z
        .array(z
        .object({
        id: nodeId,
        x: finite,
        y: finite,
        yaw: finite,
        kind: z.string().min(1),
        name: z.string().min(1),
        home: nodeId.optional(),
        skip_align: z.boolean().optional(),
    })
        .passthrough())
        .min(1),
    edges: z
        .array(z
        .object({
        edge_id: z.string().min(1),
        source_node: nodeId,
        target_node: nodeId,
        /** The harness writes RECOMMENDED for every edge it keeps. */
        status: z.string().optional(),
        x: z.array(finite).min(2),
        y: z.array(finite),
        yaw: z.array(finite),
        time: z.array(finite).optional(),
        /** What the robot routes by (edge_weight: cost, its default); the harness writes the travel time. */
        cost: z
            .object({ total: z.number({ required_error: "Required: the robot routes by it (edge_weight: cost)" }).finite().nonnegative(NONNEGATIVE) }, { required_error: "Required: the robot routes by cost.total (edge_weight: cost)" })
            .passthrough(),
        metrics: z.object({ travel_time: weight.optional(), path_length: weight.optional(), minimum_clearance: finite.optional() }).passthrough().optional(),
        feasibility: z.object({ violations: z.array(z.object({ name: z.string().optional() }).passthrough()).optional() }).passthrough().optional(),
    })
        .passthrough())
        .min(1, "The network has no edges"),
    /** "<source id>-><target id>": edge_id. */
    best_edge_per_connection: z.record(z.string()).optional(),
})
    .passthrough()
    .superRefine((n, ctx) => {
    const ids = new Set();
    const names = new Set();
    n.nodes.forEach((node, i) => {
        if (ids.has(node.id))
            ctx.addIssue({ code: "custom", path: ["nodes", i, "id"], message: `Node id ${node.id} appears twice` });
        if (names.has(node.name))
            ctx.addIssue({ code: "custom", path: ["nodes", i, "name"], message: `Node name '${node.name}' appears twice` });
        ids.add(node.id);
        names.add(node.name);
    });
    const edgeIds = new Set();
    n.edges.forEach((e, i) => {
        if (edgeIds.has(e.edge_id))
            ctx.addIssue({ code: "custom", path: ["edges", i, "edge_id"], message: `Edge id '${e.edge_id}' appears twice` });
        edgeIds.add(e.edge_id);
        for (const end of ["source_node", "target_node"])
            if (!ids.has(e[end]))
                ctx.addIssue({ code: "custom", path: ["edges", i, end], message: `Node ${e[end]} is not in the node list` });
        for (const k of ["y", "yaw", "time"]) {
            const v = e[k];
            if (v && v.length !== e.x.length)
                ctx.addIssue({ code: "custom", path: ["edges", i, k], message: `${v.length} samples where x has ${e.x.length}` });
        }
    });
});
//# sourceMappingURL=route-plans.js.map